import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, Moon, Globe, Shield, ChevronRight, Fingerprint } from 'lucide-react';
import { T, F, bd } from '@/lib/dispatchApi';
import DispatchButton from '@/components/dispatch/DispatchButton';
import {
  deletePasskey,
  formatPasskeyWhen,
  listPasskeys,
  passkeyUserMessage,
  registerPasskey,
  webAuthnAvailable,
} from '@/lib/passkeys';

export default function DispatchSettingsPage() {
  const navigate = useNavigate();
  const [darkMode] = useState(true);
  const [language] = useState('English (Canada)');
  const [keys, setKeys] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const supported = webAuthnAvailable();

  const refresh = useCallback(async () => {
    try {
      setKeys(await listPasskeys());
    } catch (err) {
      setMsg(passkeyUserMessage(err, 'list') || '');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const onAdd = async () => {
    setMsg('');
    setBusy(true);
    try {
      await registerPasskey();
      await refresh();
    } catch (err) {
      const text = passkeyUserMessage(err, 'register');
      if (text) setMsg(text);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id) => {
    setMsg('');
    setBusy(true);
    try {
      await deletePasskey(id);
      setKeys((prev) => prev.filter((k) => k.id !== id));
    } catch (err) {
      const text = passkeyUserMessage(err, 'register');
      if (text) setMsg(text);
    } finally {
      setBusy(false);
    }
  };

  const Row = ({ Icon, label, sub, value, chevron = false, onClick, last = false }) => (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', padding: '14px 16px', gap: 12,
        cursor: onClick ? 'pointer' : 'default',
        ...(!last ? { borderBottom: `1px solid ${T.nightHair}` } : {}),
      }}
      className={onClick ? 'bx-tap' : undefined}
    >
      <Icon size={14} color={T.moonMute} strokeWidth={1.5} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: 14, color: T.moon, margin: '0 0 2px 0' }}>{label}</p>
        {sub && <p style={{ fontSize: 11, color: T.moonFaint, margin: 0 }}>{sub}</p>}
      </div>
      {value && <span style={{ fontFamily: F.mono, fontSize: 11, color: T.moonMute }}>{value}</span>}
      {chevron && <ChevronRight size={14} color={T.moonFaint} strokeWidth={1.5} />}
    </div>
  );

  return (
    <div style={{ backgroundColor: T.night, minHeight: '100vh', color: T.moon, fontFamily: F.heading }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px 10px 8px', borderBottom: `1px solid ${T.nightHair}` }}>
        <button
          type="button"
          aria-label="Back"
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', width: 44, height: 44, padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <ArrowLeft size={18} color={T.moonMute} strokeWidth={1.5} />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <Settings size={15} color={T.moonMute} strokeWidth={1.5} />
          <span style={{ fontSize: 15, fontWeight: 500, color: T.moon }}>Settings</span>
        </div>
      </div>

      <div style={{ padding: '24px 20px' }}>
        {/* Appearance */}
        <p style={{ fontSize: 13, fontWeight: 500, color: T.moonFaint, marginBottom: 12 }}>Appearance</p>
        <div style={{ backgroundColor: T.nightSoft, ...bd(T.nightHair), borderRadius: 2, marginBottom: 28 }}>
          <Row Icon={Moon} label="Theme" sub="Dispatch uses dark mode" value="Dark" last />
        </div>

        {/* Sign-in */}
        <p style={{ fontSize: 13, fontWeight: 500, color: T.moonFaint, marginBottom: 12 }}>Sign-in</p>
        <div style={{ backgroundColor: T.nightSoft, ...bd(T.nightHair), borderRadius: 2, marginBottom: 28 }}>
          <Row
            Icon={Fingerprint}
            label="Passkeys"
            sub={supported ? 'Face, fingerprint, or a security key. Email and Google stay available.' : 'This browser does not support passkeys.'}
            last={keys.length === 0 && !msg}
          />
          {keys.map((k, i) => (
            <Row
              key={k.id}
              Icon={Fingerprint}
              label={k.friendly_name || 'Passkey'}
              sub={formatPasskeyWhen(k.created_at) ? `Added ${formatPasskeyWhen(k.created_at)}` : undefined}
              value="Remove"
              onClick={busy ? undefined : () => onDelete(k.id)}
              last={i === keys.length - 1 && !msg}
            />
          ))}
          {msg ? (
            <p style={{ fontSize: 12, color: T.badText, margin: 0, padding: '12px 16px' }}>{msg}</p>
          ) : null}
        </div>
        {supported && (
          <div style={{ marginTop: -16, marginBottom: 28 }}>
            <DispatchButton variant="main" data-testid="settings-add-passkey" loading={busy} onClick={onAdd}>Add a passkey</DispatchButton>
          </div>
        )}

        {/* Localisation */}
        <p style={{ fontSize: 13, fontWeight: 500, color: T.moonFaint, marginBottom: 12 }}>Localisation</p>
        <div style={{ backgroundColor: T.nightSoft, ...bd(T.nightHair), borderRadius: 2, marginBottom: 28 }}>
          <Row Icon={Globe} label="Language" value={language} last />
        </div>

        {/* Legal */}
        <p style={{ fontSize: 13, fontWeight: 500, color: T.moonFaint, marginBottom: 12 }}>Legal</p>
        <div style={{ backgroundColor: T.nightSoft, ...bd(T.nightHair), borderRadius: 2, marginBottom: 28 }}>
          <Row
            Icon={Shield}
            label="Contractor agreement v1.1"
            sub="Your independent contractor agreement"
            chevron
            onClick={() => window.open('https://dispatch-api.brxs.ca/api/onboarding/ic-agreement', '_blank')}
          />
          <Row
            Icon={Shield}
            label="Privacy policy"
            sub="How Bricks handles your data"
            chevron
            onClick={() => window.open('https://brokerage.brxs.ca/privacy', '_blank')}
            last
          />
        </div>

        <p style={{ fontSize: 12, color: T.moonFaint, textAlign: 'center' }}>
          Bricks · v1.0.0
        </p>
      </div>
    </div>
  );
}
